

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from "@/shared/ui/button";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import {
    Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage,
} from "@/shared/ui/form";
import { TruckDriver, TruckDriverInsert, TruckDriverUpdate } from '@/types';

const formSchema = z.object({
    name: z.string().min(1, { message: "氏名は必須です。" }),
    daily_rate: z.coerce.number().int().positive({ message: "レートは正の整数で入力してください。" }).nullable(),
});

type TruckDriverFormData = z.infer<typeof formSchema>;

/**
 * トラック運転手フォームダイアログのprops
 */
interface TruckDriverFormDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: TruckDriverInsert | TruckDriverUpdate) => Promise<boolean>;
    defaultValues?: TruckDriver | null;
    isUpdate: boolean;
}

/**
 * トラック運転手フォームダイアログ
 * @param isOpen ダイアログが開いているかどうか
 * @param onClose ダイアログを閉じる
 * @param onSubmit フォームの送信処理
 * @param defaultValues デフォルト値
 * @param isUpdate 更新かどうか
 * @returns トラック運転手フォームダイアログ
 */
export default function TruckDriverFormDialog({ isOpen, onClose, onSubmit, defaultValues, isUpdate }: TruckDriverFormDialogProps) {
    const form = useForm<TruckDriverFormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: defaultValues?.name ?? '',
            daily_rate: defaultValues?.daily_rate ?? null,
        },
    });

    React.useEffect(() => {
        form.reset({
            name: defaultValues?.name ?? '',
            daily_rate: defaultValues?.daily_rate ?? null,
        });
    }, [defaultValues, form]);

    /**
     * フォームの送信処理
     * @param values フォームの値
     */
    const handleFormSubmit = async (values: TruckDriverFormData) => {
        const dataToSubmit = {
            ...values,
            daily_rate: values.daily_rate === 0 ? null : values.daily_rate,
        };
        // 更新時はIDを含める
        const dataWithId = isUpdate && defaultValues?.id
            ? { ...dataToSubmit, id: defaultValues.id, updated_by: 'current-user' } as TruckDriverUpdate
            : { ...dataToSubmit, created_by: 'current-user', updated_by: 'current-user' } as TruckDriverInsert;

        const success = await onSubmit(dataWithId);
        if (success) {
            form.reset();
            onClose();
        }
    };

    /**
     * トラック運転手フォームダイアログの表示
     * @returns トラック運転手フォームダイアログ
     */
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{isUpdate ? 'ドライバー情報の編集' : '新規ドライバーの追加'}</DialogTitle>
                    <DialogDescription>
                        {isUpdate ? 'ドライバーの情報を更新します。' : '新しいドライバーの情報を入力してください。'}
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4 py-4">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>氏名</FormLabel>
                                    <FormControl>
                                        <Input placeholder="鈴木 一郎" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="daily_rate"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>レート (円/日など)</FormLabel>
                                    <FormControl>
                                        <Input type="number" placeholder="例: 18000" {...field} value={field.value ?? ''} />
                                    </FormControl>
                                    <FormDescription>
                                        任意項目です。1日あたりなどのレートを入力します。
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose}>キャンセル</Button>
                            <Button type="submit" disabled={form.formState.isSubmitting}>
                                {form.formState.isSubmitting ? '保存中...' : (isUpdate ? '更新' : '追加')}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
} 